import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NAV, canAccess } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Loader2, Eye, Smartphone, Check } from "lucide-react";
import PushNotificationsCard from "@/components/PushNotificationsCard";

const EMAIL_TYPES = [
  { k: "quote_submitted", label: "Quote awaiting my approval" },
  { k: "quote_approved", label: "My quote was approved" },
  { k: "deal_created", label: "New enquiry created" },
  { k: "deal_won", label: "Deal won" },
  { k: "portal_decision", label: "Client accepted / declined" },
];

const TABS = [
  ["push", "Push notifications"],
  ["email", "Email notifications"],
  ["mobile", "Mobile shortcuts"],
];

// PreferencesDialog — personal settings (gear icon, bottom-left of the sidebar):
// push notification prefs, personal email opt-outs and the mobile bottom-bar shortcuts.
export function PreferencesDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState("push");
  const { data: prefs, isFetching } = useQuery({
    queryKey: ["me-preferences"],
    queryFn: async () => (await api.get("/auth/me/preferences")).data,
    enabled: open,
  });

  const [draft, setDraft] = useState(null);
  useEffect(() => { if (prefs && open) setDraft(prefs); }, [prefs, open]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/auth/me/preferences", draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me-preferences"] });
      toast.success("Preferences saved");
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save preferences"),
  });

  const previewMut = useMutation({
    mutationFn: async () => (await api.get("/digest/personal-preview")).data,
    onSuccess: (data) => {
      const w = window.open("", "_blank");
      if (w) { w.document.write(data.html); w.document.title = data.subject; }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not build preview"),
  });

  const emailPrefs = draft?.email_prefs || {};
  const setEmailPref = (k, v) => setDraft({ ...draft, email_prefs: { ...emailPrefs, [k]: v } });

  const picks = draft?.mobile_tabs || [];
  const modules = NAV.filter((n) => canAccess(user, n)).filter((n) => n.to !== "/dashboard");
  const toggleModule = (to) => {
    if (picks.includes(to)) return setDraft({ ...draft, mobile_tabs: picks.filter((t) => t !== to) });
    if (picks.length >= 4) { toast.error("Maximum 4 shortcuts — untick one first"); return; }
    setDraft({ ...draft, mobile_tabs: [...picks, to] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="preferences-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Preferences</DialogTitle>
          <DialogDescription className="text-xs">Personal settings — they only apply to you.</DialogDescription>
        </DialogHeader>

        <div className="flex border-b border-slate-200 -mt-1">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" data-testid={`prefs-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${tab === k ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "push" && (
          <div data-testid="prefs-push-panel">
            <PushNotificationsCard embedded />
            <p className="text-[11px] text-slate-400 mt-2">Push settings save instantly — no need to press Save.</p>
          </div>
        )}

        {tab === "email" && (isFetching && !draft ? (
          <div className="py-8 flex items-center justify-center text-slate-400 text-sm"><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>
        ) : draft && (
          <div className="space-y-4" data-testid="prefs-email-panel">
            <Card className="rounded-sm border-slate-200 p-3 flex items-start gap-3">
              <div className="w-9 h-9 shrink-0 bg-amber-100 text-amber-700 flex items-center justify-center rounded-sm"><Mail size={15} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-900">Daily digest</div>
                  <Switch data-testid="digest-opt-in-toggle" checked={!!draft.digest_opt_in}
                    onCheckedChange={(v) => setDraft({ ...draft, digest_opt_in: v })} />
                </div>
                <div className="text-[12px] text-slate-500 leading-relaxed mt-0.5">
                  Lists your bookings, install jobs, assigned tasks and pending approvals for the day. Skipped when you've got nothing on.
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-slate-600 font-medium">Send at</span>
                  <input type="time" data-testid="digest-time-input" value={draft.digest_time || "06:00"}
                    disabled={!draft.digest_opt_in}
                    onChange={(e) => setDraft({ ...draft, digest_time: e.target.value })}
                    className="h-8 border border-slate-200 rounded-sm px-2 text-xs text-slate-800 disabled:opacity-50" />
                  <span className="text-[11px] text-slate-400">Sydney time</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => previewMut.mutate()} disabled={previewMut.isPending}
                  className="rounded-sm h-8 text-xs mt-2.5" data-testid="digest-preview-btn">
                  <Eye size={12} className="mr-1.5" />{previewMut.isPending ? "Building…" : "Preview today's digest"}
                </Button>
              </div>
            </Card>
            <div>
              <div className="stamp-label text-slate-600 mb-2">Automatic team emails — untick what you don't want</div>
              <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
                {EMAIL_TYPES.map((t) => (
                  <label key={t.k} className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
                    <Switch data-testid={`email-pref-${t.k}`} checked={emailPrefs[t.k] !== false}
                      onCheckedChange={(v) => setEmailPref(t.k, v)} />
                    <span>{t.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                You only ever receive the types that apply to your role — switching one off removes <b>you</b> from that email; it keeps sending to everyone else. Admin switches in Email Automations still control each type globally.
              </p>
            </div>
          </div>
        ))}

        {tab === "mobile" && (isFetching && !draft ? (
          <div className="py-8 flex items-center justify-center text-slate-400 text-sm"><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>
        ) : draft && (
          <div className="space-y-3" data-testid="prefs-mobile-panel">
            <div className="flex items-center justify-between">
              <div className="stamp-label text-slate-600 flex items-center gap-1.5"><Smartphone size={13} /> Choose your bottom-bar shortcuts</div>
              <div className="text-xs font-bold text-cyan-700" data-testid="mobile-tabs-count">{picks.length} of 4 selected</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {modules.map((m) => {
                const sel = picks.includes(m.to);
                const Icon = m.icon;
                return (
                  <button key={m.to} type="button" onClick={() => toggleModule(m.to)}
                    data-testid={`mobile-tab-pick-${m.to.replace(/^\//, "").replace(/\//g, "-")}`}
                    className={`flex items-center gap-2.5 border rounded-sm px-2.5 py-2 text-[13px] text-left transition-colors ${sel ? "border-cyan-500 bg-cyan-50 text-cyan-900" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    <span className={`w-4 h-4 shrink-0 border rounded-sm flex items-center justify-center ${sel ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`}>
                      {sel && <Check size={11} className="text-white" />}
                    </span>
                    <Icon size={14} className="shrink-0" />
                    <span className="truncate">{m.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              <b>Home is always the first tab</b> — you pick up to 4 more, shown in the order you select them.
              Leave it empty to keep the standard set (Tasks · Enquiry · Scheduler · Jobs).
            </p>
          </div>
        ))}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">{tab === "push" ? "Close" : "Cancel"}</Button>
          {tab !== "push" && (
            <Button data-testid="save-preferences-btn" onClick={() => save.mutate()}
              disabled={!draft || save.isPending} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              {save.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              Save preferences
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
